import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@evertrue/et-components';
import EverTrue from 'app';
import useSavedSearches from '../../hooks/use-saved-searches';

/**
 * Displays active audience sources attached to a cadence.
 * Saved search sources are ongoing (auto-syncing) — users can see and disconnect them.
 * List and individual contact additions are shown as historical counts.
 */
const CadenceAudienceSources = ({ audiences = [], onDisconnect, canManage }) => {
  const savedSearches = useSavedSearches();
  const [disconnecting, setDisconnecting] = useState(null);

  if (!audiences || audiences.length === 0) return null;

  const savedSearchAudiences = audiences.filter((a) => a.audienceType === 'saved_search' && a.savedSearchTagId);
  const listAudiences = audiences.filter((a) => a.audienceType === 'list');
  const individualCount = audiences.filter((a) => a.audienceType === 'individual').length;

  // Only render if there's something meaningful to show
  if (savedSearchAudiences.length === 0 && listAudiences.length === 0 && individualCount === 0) {
    return null;
  }

  const resolveName = (tagId) => {
    const match = savedSearches.find((s) => String(s.value) === String(tagId));
    return match?.label || `Saved Search (ID ${tagId})`;
  };

  const handleDisconnect = async (audience) => {
    setDisconnecting(audience.id);
    try {
      await onDisconnect(audience.id);
    } catch (err) {
      EverTrue.Alert.error(`Failed to disconnect: ${err.message}`);
    } finally {
      setDisconnecting(null);
    }
  };

  return (
    <div className="cadence-audience-sources">
      <h3 className="cadence-audience-sources--title">Audience Sources</h3>

      {savedSearchAudiences.map((audience) => (
        <div key={audience.id} className="cadence-audience-sources--row cadence-audience-sources--saved-search">
          <div className="cadence-audience-sources--info">
            <Icon icon="sync" className="cadence-audience-sources--icon" />
            <div>
              <span className="cadence-audience-sources--name">{resolveName(audience.savedSearchTagId)}</span>
              <span className="cadence-audience-sources--badge">Active source</span>
            </div>
          </div>
          {canManage && (
            <Button
              type="simple"
              className="cadence-audience-sources--disconnect"
              disabled={disconnecting === audience.id}
              onClick={() => handleDisconnect(audience)}
            >
              {disconnecting === audience.id ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          )}
        </div>
      ))}

      {(listAudiences.length > 0 || individualCount > 0) && (
        <div className="cadence-audience-sources--historical">
          <Icon icon="history" className="cadence-audience-sources--icon" />
          <span className="cadence-audience-sources--historical-label">
            Previously added:
            {listAudiences.length > 0 && ` ${listAudiences.length} list import${listAudiences.length !== 1 ? 's' : ''}`}
            {listAudiences.length > 0 && individualCount > 0 && ','}
            {individualCount > 0 && ` ${individualCount} individual contact${individualCount !== 1 ? 's' : ''}`}
          </span>
        </div>
      )}
    </div>
  );
};

CadenceAudienceSources.propTypes = {
  audiences: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      audienceType: PropTypes.string,
      savedSearchTagId: PropTypes.string,
      contactListId: PropTypes.string,
      contactId: PropTypes.number,
      autoAddEnabled: PropTypes.bool,
    })
  ),
  onDisconnect: PropTypes.func.isRequired,
  canManage: PropTypes.bool,
};

export default CadenceAudienceSources;
